import { DateTime } from "luxon";

export const RESTARTED_QUESTION_ID = "Restarted";

type StartTestSection = {
    id?: number;
    sectionId?: number;
    order?: number;
    formId?: number;
    name?: string;
};

type RestartPage = {
    sectionId?: number;
    content?: string;
};

type StartTestPayload = {
    restartPage?: RestartPage | null;
    lastSectionIdCompleted?: number | null;
    isExistingTestInstance?: boolean;
    testData?: {
        id?: number;
        code?: string;
        companyId?: number;
        languageId?: number;
        stateId?: number;
    } | null;
    languageId?: number;
    testTypes?: Array<{
        sections?: StartTestSection[];
    }>;
};

function sortedSections(payload: StartTestPayload | null | undefined): StartTestSection[] {
    const sections = payload?.testTypes?.[0]?.sections;
    if (!Array.isArray(sections)) return [];
    return [...sections].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
}

export type SectionResumeState = {
    sectionId?: number;
    page?: number;
    answers?: Record<string, string>;
};

/** Page and answers to reopen for this section. Other sections start empty on page 1. */
export function sectionResumeFor(
    resume: SectionResumeState | null | undefined,
    sectionId: number
): { page: number; answers?: Record<string, string> } {
    const id = Number(sectionId || 0);
    if (!resume || id <= 0 || Number(resume.sectionId) !== id) {
        return { page: 1 };
    }

    const answers = resume.answers && typeof resume.answers === "object" ? resume.answers : undefined;
    return {
        page: Math.max(1, Number(resume.page) || 1),
        answers,
    };
}

export function resolveResumeSection(payload: StartTestPayload | null | undefined): StartTestSection | null {
    const sections = sortedSections(payload);
    if (sections.length === 0) return null;

    const overlaySectionId = Number(payload?.restartPage?.sectionId || 0);
    if (overlaySectionId > 0) {
        return sections.find((section) => Number(section.id || section.sectionId || 0) === overlaySectionId) ?? null;
    }

    const lastCompletedId = Number(payload?.lastSectionIdCompleted || 0);
    if (lastCompletedId > 0) {
        const lastCompleted = sections.find((section) => Number(section.id || section.sectionId || 0) === lastCompletedId);
        if (lastCompleted) {
            return sections.find((section) => Number(section.order || 0) > Number(lastCompleted.order || 0)) ?? null;
        }
    }

    return null;
}

export function suppressRestartForActiveSession<T extends { restartPage?: RestartPage | null } | null | undefined>(
    payload: T,
    hasActiveSession: boolean
): T {
    if (!payload || !hasActiveSession || payload.restartPage == null) return payload;
    const content = String(payload.restartPage?.content || '').trim();
    if (content) return payload;
    return { ...payload, restartPage: null };
}

export function shouldRecordSilentRestart(
    payload: StartTestPayload | null | undefined,
    hasActiveSession: boolean = false
): boolean {
    if (hasActiveSession) return false;
    if (!payload || payload.restartPage) return false;
    const stateId = Number(payload.testData?.stateId || 0);
    if (stateId !== 210) return false;
    return resolveResumeSection(payload) != null;
}

export function buildRestartedRealtimePostData(
    payload: StartTestPayload | null | undefined,
    section: StartTestSection | null | undefined
) {
    const clientTime = DateTime.now().toISO();
    return {
        questionId: RESTARTED_QUESTION_ID,
        response: "1",
        Name: section?.name || "Restarted",
        FormId: Number(section?.formId || 0),
        SectionId: Number(section?.id || section?.sectionId || 0) || undefined,
        Code: payload?.testData?.code ?? "demo",
        LanguageId: payload?.languageId ?? payload?.testData?.languageId,
        TestId: payload?.testData?.id ?? 0,
        CompanyId: payload?.testData?.companyId ?? 0,
        ClientTime: clientTime,
        When: clientTime,
    };
}
